import React from 'react';
import { Link } from 'react-router-dom';
import HeroSlider from '@/components/HeroSlider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Users, Stethoscope, Activity, Calendar, MessageCircle, Clock, Award } from 'lucide-react';

const Home = () => {
  const quickFacts = [
    { icon: <Stethoscope className="w-6 h-6" />, label: 'Specialty', value: 'Cardiology & Community Health' },
    { icon: <Award className="w-6 h-6" />, label: 'Experience', value: '25+ years' },
    { icon: <Users className="w-6 h-6" />, label: 'Patients Helped', value: 'Thousands daily' },
    { icon: <Clock className="w-6 h-6" />, label: 'OPD Hours', value: '5:00 AM - 11:00 AM' },
  ];

  const services = [
    {
      icon: <Heart className="w-8 h-8 text-cyan-600" />,
      title: 'Cardiology Care',
      description: 'Specialized cardiac evaluation and treatment for heart conditions'
    },
    {
      icon: <Activity className="w-8 h-8 text-emerald-600" />,
      title: 'Diabetes Management',
      description: 'Remote monitoring with implantable glucose tracking technology'
    },
    {
      icon: <Stethoscope className="w-8 h-8 text-blue-600" />,
      title: 'General Consultation',
      description: 'Comprehensive health checkups and medical consultations'
    },
    {
      icon: <Users className="w-8 h-8 text-purple-600" />,
      title: 'Community Outreach',
      description: 'Free mobile clinics at temples, mosques, and community centers'
    },
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Slider */}
      <HeroSlider />

      {/* Quick Facts Strip */}
      <section className="bg-gradient-to-r from-cyan-600 to-emerald-600 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {quickFacts.map((fact, index) => (
              <div
                key={index}
                className="text-center text-white"
                data-testid={`quick-fact-${index}`}
              >
                <div className="flex justify-center mb-2">{fact.icon}</div>
                <div className="text-sm opacity-90">{fact.label}</div>
                <div className="font-bold text-lg">{fact.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Free, comprehensive healthcare services for KGF mine workers and their families
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card
                key={index}
                className="card-hover border-2 border-gray-100 hover:border-cyan-200"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Doctor Story Preview */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                Dr. B. Rajendra Kumar
              </h2>
              <h3 className="text-2xl text-cyan-700 font-semibold mb-4">
                A Lifeline for KGF
              </h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                When the Kolar Gold Fields fell silent, the health of its people spoke loudly. 
                For over 25 years, Dr. Kumar has run a charitable hospital and outreach clinics 
                that deliver free, modern healthcare to thousands affected by unemployment, 
                toxic exposure, and poverty.
              </p>
              <Link to="/about">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700">
                  Read Full Story
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758691462126-2ee47c8bf9e7?w=800&q=80"
                alt="Doctor with patients"
                className="rounded-2xl shadow-2xl w-full"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold text-cyan-600">25+</div>
                <div className="text-sm text-gray-600">Years of Service</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Book Your Free Appointment Today
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Early morning OPD available. Walk-ins welcome, but booking ensures your slot.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/appointments" data-testid="cta-book-appointment">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100">
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
            </Link>
            <a
              href="https://wa.me/919876543210"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
            >
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 md:p-12 rounded-2xl shadow-lg">
            <div className="text-6xl text-cyan-600 mb-4">“</div>
            <p className="text-2xl text-gray-800 font-medium mb-6 italic">
              He is a living God to me — he saved my life and saved my family from medical ruin.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold">
                S
              </div>
              <div>
                <div className="font-semibold text-gray-900">Selva, 70</div>
                <div className="text-sm text-gray-600">Ongoing cardiac patient</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
